"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  buildCribnoshAppSchemeUrl,
  detectClientPlatform,
  getCribnoshAppLinkConfig,
  getPlatformInstallHref,
  resolveCribnoshAppLaunchTarget,
  type ClientPlatform,
  type CribnoshAppId,
} from "@/lib/mobile-app-links";

type RouteToCribnoshAppOptions = {
  appId: CribnoshAppId;
  appPath?: string;
  webFallbackHref?: string;
  openDelayMs?: number;
  fallbackDelayMs?: number;
};

type UseAdaptiveCribnoshAppLaunchOptions = {
  appId: CribnoshAppId;
  appPath?: string;
  webFallbackHref?: string;
  deepLinkingEnabled?: boolean;
  autoOpenEnabled?: boolean;
  autoOpenKey?: string;
};

function getBrowserPlatform(): ClientPlatform {
  if (typeof window === "undefined") {
    return "desktop";
  }

  return detectClientPlatform(
    window.navigator.userAgent,
    window.navigator.maxTouchPoints,
  );
}

function getAttemptStorageKey(appId: CribnoshAppId, appPath: string): string {
  return `cribnosh:adaptive-app-launch:${appId}:${appPath}`;
}

function hasAttemptedAutoOpen(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markAutoOpenAttempted(key: string): void {
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Storage can be unavailable in private browsing; auto-open can still proceed.
  }
}

export function routeToCribnoshApp({
  appId,
  appPath = "/",
  webFallbackHref,
  openDelayMs = 80,
  fallbackDelayMs = 1400,
}: RouteToCribnoshAppOptions): ClientPlatform {
  if (typeof window === "undefined") {
    return "desktop";
  }

  const platform = getBrowserPlatform();
  const { canOpenApp, resolvedAppPath, resolvedWebFallbackHref } =
    resolveCribnoshAppLaunchTarget(appId, appPath, webFallbackHref);
  const config = getCribnoshAppLinkConfig(appId);

  if (platform === "desktop" || !canOpenApp) {
    window.location.assign(resolvedWebFallbackHref);
    return platform;
  }

  const deepLinkUrl = buildCribnoshAppSchemeUrl(appId, resolvedAppPath);
  const installHref = getPlatformInstallHref(appId, platform);

  let completed = false;

  const complete = () => {
    completed = true;
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", complete);
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      complete();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange, {
    passive: true,
  });
  window.addEventListener("pagehide", complete, { once: true });

  window.setTimeout(() => {
    window.location.assign(deepLinkUrl);
  }, openDelayMs);

  window.setTimeout(() => {
    if (completed || document.visibilityState === "hidden") {
      return;
    }

    complete();
    window.location.assign(installHref);
  }, fallbackDelayMs);

  return platform;
}

export function useAdaptiveCribnoshAppLaunch({
  appId,
  appPath = "/",
  webFallbackHref,
  deepLinkingEnabled = true,
  autoOpenEnabled = false,
  autoOpenKey,
}: UseAdaptiveCribnoshAppLaunchOptions) {
  const [platform, setPlatform] = useState<ClientPlatform>("desktop");
  const launchTarget = useMemo(
    () => resolveCribnoshAppLaunchTarget(appId, appPath, webFallbackHref),
    [appId, appPath, webFallbackHref],
  );
  const resolvedWebFallbackHref = launchTarget.resolvedWebFallbackHref;

  useEffect(() => {
    setPlatform(getBrowserPlatform());
  }, []);

  const normalizedAppPath = launchTarget.resolvedAppPath;
  const canOpenApp = launchTarget.canOpenApp;

  const openApp = useCallback(() => {
    if (!deepLinkingEnabled || !canOpenApp) {
      window.location.assign(resolvedWebFallbackHref);
      return;
    }

    routeToCribnoshApp({
      appId,
      appPath: normalizedAppPath,
      webFallbackHref: resolvedWebFallbackHref,
    });
  }, [
    appId,
    canOpenApp,
    deepLinkingEnabled,
    normalizedAppPath,
    resolvedWebFallbackHref,
  ]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !deepLinkingEnabled ||
      !canOpenApp ||
      !autoOpenEnabled ||
      platform === "desktop"
    ) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get("open_app") === "0") {
      return;
    }

    const attemptKey =
      autoOpenKey || getAttemptStorageKey(appId, normalizedAppPath);
    if (hasAttemptedAutoOpen(attemptKey)) {
      return;
    }

    markAutoOpenAttempted(attemptKey);
    openApp();
  }, [
    appId,
    autoOpenEnabled,
    autoOpenKey,
    canOpenApp,
    deepLinkingEnabled,
    normalizedAppPath,
    openApp,
    platform,
  ]);

  return {
    platform,
    isMobilePlatform: platform !== "desktop",
    canOpenApp,
    openApp,
    installHref: getPlatformInstallHref(appId, platform),
    webFallbackHref: resolvedWebFallbackHref,
  };
}
